import React, { useEffect } from 'react';
import { RadioGroup, RadioOption } from '@/components/design-system/molecules/radio-group';
import { HeroIcons } from '@/components/design-system/atoms/icon';
import { TextInput } from '@/components/design-system/atoms/text/text-input';

export type NavigationType = 'none' | 'link' | 'in_app_page';

interface NavigationTypeSelectorProps {
  selectedType: NavigationType;
  onTypeChange: (type: NavigationType) => void;
  urlValue?: string;
  onUrlChange?: (url: string) => void;
  appDeepLink?: string;
  onAppDeepLinkChange?: (link: string) => void;
  buttonLabel?: string;
  onButtonLabelChange?: (label: string) => void;
  disabled?: boolean;
}

export const NavigationTypeSelector: React.FC<NavigationTypeSelectorProps> = ({
  selectedType,
  onTypeChange,
  urlValue = '',
  onUrlChange,
  buttonLabel = '',
  onButtonLabelChange,
  disabled = false,
}) => {
  const navigationOptions: RadioOption[] = [
    { id: 'none', label: 'No Navigation' },
    { id: 'link', label: 'URL Navigate' },
    { id: 'in_app_page', label: 'In-App Page' },
  ];

  useEffect(() => {
    if (disabled) {
      onTypeChange('none');
    }
  }, [disabled, onTypeChange]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <p className="text-body font-bold text-text-primaryHeavy">Tap to navigate</p>
        <RadioGroup
          options={navigationOptions}
          value={selectedType}
          onChange={(value) => onTypeChange(value as NavigationType)}
          disabled={disabled}
        />
      </div>

      {!disabled && selectedType === 'link' && (
        <>
          <TextInput
            label="Enter URL"
            placeholder="https://www.google.com"
            required
            value={urlValue}
            className="rounded-[12px] bg-white hover:bg-white"
            onChange={(e) => onUrlChange?.(e.target.value)}
          />

          <TextInput
            label="Button Label"
            placeholder="e.g. Read More"
            required
            value={buttonLabel}
            className="rounded-[12px] bg-white hover:bg-white"
            onChange={(e) => onButtonLabelChange?.(e.target.value)}
          />
        </>
      )}

      {!disabled && selectedType === 'in_app_page' && (
        <>
          <TextInput
            label="App Deep Link"
            placeholder="e.g. myapp://home"
            required
            value={urlValue}
            className="rounded-[12px] bg-white hover:bg-white"
            onChange={(e) => onUrlChange?.(e.target.value)}
          />

          <TextInput
            label="Button Label"
            placeholder="e.g. Read More"
            required
            value={buttonLabel}
            className="rounded-[12px] bg-white hover:bg-white"
            onChange={(e) => onButtonLabelChange?.(e.target.value)}
            showHelpMessage
            helpMessage="The button label will be displayed on the button that navigates to the app deep link."
          />
        </>
      )}

      {disabled && (
        <div className="mt-2 flex flex-row items-start gap-2 rounded-[8px] border border-solid border-border-primaryLight bg-bg-primaryMedium p-[12px]">
          <HeroIcons name="information-circle" size={18} />
          <p className="text-body font-medium text-text-primaryHeavy">
            Tap to Navigate does not work with longer announcement messages. If the message reaches
            or exceeds 80 characters, you won't be able to change the tap action. Instead, a 'Read More' button
            will appear, allowing users to open the full message when tapped.
          </p>
        </div>
      )}
    </div>
  );
};
